import { getPublicEnv } from "@janeway/app-sdk/public-env";
/**
 * Theme-Sync Cookies — geteilt ueber alle Janeway-Subdomains.
 *
 * Ein User toggelt Light/Dark oder eine Farbvariante in App A → Cookie wird
 * auf der Parent-Domain geschrieben (z.B. ".janewaylabs.com") → beim Oeffnen
 * von App B ist der Zustand sofort bekannt (SSR liest Cookie, rendert das
 * richtige Theme ins HTML, kein Flash).
 */

export const THEME_COOKIE = "janeway-theme";
export const COLOR_THEME_COOKIE = "janeway-color-theme";
/**
 * Eigene Farbanpassungen als JSON (`{"dark":{"border":"hsl(0 0% 20%)"},"light":{}}`).
 *
 * WARUM ALS COOKIE: bis 0.279.1 lagen sie ausschliesslich in `user_preferences`
 * und im localStorage — beides erreicht der Server beim Rendern nicht. Der erste
 * Paint zeigte deshalb das unveraenderte Farbschema und kippte nach der
 * Hydration auf den eigenen Wert. Gemessen am 2026-08-21 in ai-chat: `--border`
 * sprang nach 878 ms von `hsl(217 33% 29%)` auf `hsl(0 0% 20%)`, der Rahmen des
 * Eingabefeldes wurde dabei sichtbar grauer. Das traf JEDEN Nutzer mit auch nur
 * einer angepassten Farbe, bei jedem Laden — und genau das wollte
 * `resolveInitialTheme` verhindern.
 *
 * Der Cookie ist eine KOPIE, keine Quelle: es bleiben `user_preferences`
 * (geraeteuebergreifend) und der localStorage (schnell). Fehlt er oder ist er
 * kaputt, verhaelt sich alles wie vorher.
 */
export const THEME_OVERRIDES_COOKIE = "janeway-theme-overrides";
/**
 * Obergrenze fuer den Overrides-Cookie. Ein Browser garantiert nur ~4 KB PRO
 * COOKIE und begrenzt zusaetzlich die Summe pro Domain — und dieser hier faehrt
 * bei JEDER Anfrage an JEDE Janeway-App mit, Bilder und Chunks eingeschlossen.
 * Wer mehr anpasst, als hier hineinpasst, bekommt weiterhin den Nachzug nach
 * der Hydration; das ist unschoen, aber besser als abgeschnittene Anfragen.
 */
export const THEME_OVERRIDES_COOKIE_MAX = 1500;
/** Logo-Hover-Animation an/aus — wie die Theme-Cookies cross-App geteilt. "1"/"0". */
export const LOGO_HOVER_COOKIE = "janeway-logo-hover";
/**
 * Symbol-Stil des Dateimanagers ("blatt"/"kachel"/"kante") — wie die
 * Theme-Cookies cross-App geteilt.
 *
 * WARUM COOKIE UND NICHT NUR PROVIDER-STATE: Der Wert muss beim ERSTEN PAINT
 * feststehen. `resolveInitialTheme` liest ihn deshalb serverseitig und gibt ihn
 * als `initialSymbolStil` ins Layout — sonst rendert die Seite den
 * Vorgabe-Satz und tauscht nach der Hydration jedes Dateisymbol aus. Bei einer
 * Farbe war das schon auffaellig genug (`THEME_OVERRIDES_COOKIE`, 878 ms); bei
 * einer geaenderten FORM waere es ein Sprung.
 *
 * Bewusst OHNE `user_preferences`-Spalte, wie `LOGO_HOVER_COOKIE` daneben: eine
 * Darstellungsvorliebe, die der Nutzer in einem Klick neu trifft, rechtfertigt
 * keine Migration. Auf einem frischen Geraet gilt wieder die Vorgabe.
 */
export const SYMBOL_STIL_COOKIE = "janeway-symbolstil";
/** Sprachvisualisierung (Körper, Animationen, Größe, Bahnen) als JSON. */
export const VOICE_ORB_COOKIE = "janeway-voice-orb";
/**
 * Uebergangs-Effekt beim Theme-Wechsel ("none"/"soft"/"wave"/"diagonal") — wie
 * die Theme-Cookies cross-App geteilt.
 *
 * WARUM COOKIE statt nur localStorage: localStorage ist origin-gebunden. Wer den
 * Effekt in ai-chat auf "diagonal" stellte, sah in hub weiter die Welle — die
 * Einstellung kam dort schlicht nie an. Der Effekt wird ausschliesslich im Moment
 * der Interaktion gelesen, nie beim ersten Paint, deshalb ist das FOUC-frei.
 */
export const THEME_TRANSITION_COOKIE = "janeway-theme-transition";

/**
 * 30 Tage. Die Cookie-Richtlinie Ziff. 2.1 sagt das ausdruecklich zu — fuer reine
 * Darstellungsvorlieben traegt § 25 Abs. 2 Nr. 2 TDDDG und Art. 5 Abs. 1 lit. e DSGVO
 * keine laengere Frist: eine Einstellung, die der Nutzer in einem Klick neu trifft,
 * rechtfertigt keine Jahresspeicherung auf seinem Endgeraet. Stand hier bis 2026-08-23
 * auf einem Jahr, waehrend der Text 30 Tage versprach.
 * Harmlos, weil die Darstellung zusaetzlich am Konto haengt: laeuft das Cookie ab,
 * gilt wieder die Kontoeinstellung, nicht ein Vorgabewert.
 */
export const COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

function getClientCookieDomain(): string | undefined {
  // NEXT_PUBLIC_* ist zur Build-Zeit ersetzt, fuer den Browser verfuegbar.
  return getPublicEnv("NEXT_PUBLIC_COOKIE_DOMAIN");
}

/**
 * Loescht einen host-only Cookie desselben Namens.
 *
 * WARUM: Vor dem Cookie-Domain-Fix (2026-07-05) schrieben Apps ohne
 * `NEXT_PUBLIC_COOKIE_DOMAIN` host-only Cookies mit einem Jahr Laufzeit. Sie
 * liegen bis heute in den Browsern und existieren NEBEN dem geteilten
 * Parent-Domain-Cookie. `document.cookie` liefert beide; die Lese-Regex nimmt den
 * zuerst gesetzten — also den veralteten. Der Server kann sie gar nicht
 * unterscheiden, im `Cookie`-Header steht keine Domain. Ergebnis: eine App liest
 * ihren alten Wert, wendet ihn an und schreibt ihn in den GETEILTEN Cookie —
 * alle anderen Apps ziehen beim naechsten Tab-Wechsel nach.
 *
 * Ein `Max-Age=0` OHNE `Domain` trifft ausschliesslich den host-only Cookie.
 */
export function purgeHostOnlyCookie(name: string): void {
  if (typeof document === "undefined") return;
  if (!getClientCookieDomain()) return; // lokale Entwicklung: host-only ist dort korrekt
  document.cookie = `${name}=; Path=/; Max-Age=0`;
}


/**
 * ══ DIE SCHLEUSE STEHT AN DER TUER, NICHT AM AUFRUFER ══════════════════════
 *
 * Ein Theme-Keks gilt fuer die ganze Eltern-Domain (`.janewaylabs.com`) und
 * damit fuer JEDE App und jede Sitzung des Kontos. Zwei Faelle duerfen ihn
 * deshalb nicht anfassen:
 *
 *   1. **Eine abgeschottete Huelle** (`localOnly`) — der GUI-Showroom. Er
 *      probiert Themes aus; das ist keine Kontoeinstellung.
 *   2. **Ein Dokument im fremden Rahmen.** Was dort zu sehen ist, ist eine
 *      Darstellungsanweisung des Wirts, keine Wahl des Nutzers.
 *
 * ── WARUM NICHT MEHR BEIM AUFRUFER ──────────────────────────────────────────
 *
 * Genau das war der Zustand bis 0.442.1: `ColorThemeProvider` hielt eine eigene
 * Schleuse und begruendete sie damit, dass „die fuenfzehnte Schreibstelle, die
 * jemand naechstes Jahr dazuschreibt", sie nicht haette.
 *
 * **Die gab es zu dem Zeitpunkt schon — fuenfmal**, gemessen am 2026-09-17:
 *
 *     providers/ThemeProvider.tsx:100        Hell/Dunkel
 *     lib/view-mode.ts:33                    Ansichtsmodus
 *     lib/theme-transition.ts:63             Uebergangseffekt
 *     components/auth/onboarding/OnboardingApplier.tsx:69-70
 *     components/auth/onboarding/AuthOnboarding.tsx:1085-1086
 *
 * Der Waechter dazu las ausschliesslich `ColorThemeProvider.tsx` und konnte
 * keine davon sehen; der Wächter, auf den sich sein Kommentar berief
 * (`theme-nur-ueber-die-schleuse.test.ts`), existierte im Baum ueberhaupt
 * nicht. Eine Schleuse, die jeder Aufrufer selbst passieren muss, ist keine
 * Schleuse — sie ist eine Bitte.
 *
 * Hier kommt keiner vorbei: `writeThemeCookie` und `deleteThemeCookie` sind die
 * einzigen Stellen der Bibliothek, die `document.cookie` fuer diese Namen
 * setzen, und `waechter-keks-nur-durch-die-tuer.test.ts` haelt das fest.
 */
let abgeschottet = false;

/**
 * Meldet der Tuer, dass diese Huelle abgeschottet ist (`localOnly`).
 *
 * Wird beim RENDERN gesetzt, nicht im Effekt: Kind-Effekte laufen vor denen des
 * Elternteils, ein Kind koennte sonst schneller schreiben als die Sperre.
 */
export function setzeKeksAbschottung(an: boolean): void {
  abgeschottet = an;
}

/** Nur fuer Tests: die Tuer wieder oeffnen. */
export function keksAbschottungZuruecksetzen(): void {
  abgeschottet = false;
}

function imFremdenRahmen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // Der Zugriff quer ueber Herkuenfte wirft — und dass er wirft, ist selbst
    // die Antwort: dann steckt das Dokument in einem fremden Rahmen.
    return true;
  }
}

export function darfKeksSchreiben(): boolean {
  return !abgeschottet && !imFremdenRahmen();
}

/** Schreibt ein Theme-Cookie auf der Parent-Domain (wenn gesetzt). */
export function writeThemeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  if (!darfKeksSchreiben()) return;
  const domain = getClientCookieDomain();
  // Erst den Doppelgaenger raeumen, sonst ueberschattet er den Wert, den wir gleich setzen.
  if (domain) purgeHostOnlyCookie(name);

  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    `Max-Age=${COOKIE_MAX_AGE}`,
    "SameSite=Lax",
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

/** Liest ein Theme-Cookie im Browser. Fuer SSR siehe `theme-ssr.ts`. */
export function readThemeCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Loescht ein Theme-Cookie auf beiden Ebenen (Parent-Domain und host-only). */
export function deleteThemeCookie(name: string): void {
  if (typeof document === "undefined") return;
  if (!darfKeksSchreiben()) return;
  const domain = getClientCookieDomain();
  document.cookie = `${name}=; Path=/; Max-Age=0`;
  if (domain) document.cookie = `${name}=; Path=/; Max-Age=0; Domain=${domain}`;
}
